import type { BackgroundCommand } from './rebuild/background-command';
/** UI/transport contract; SQL owns membership, economics, authority and digests. */
export const PERIOD_REAPPROVAL_ATTESTATION =
  'I approve the current displayed pay inputs for these timesheets. Earlier approvals do not prove every input is unchanged.';
export type PeriodReapprovalRequest =
  | { kind: 'periodReapproval'; mode: 'PREVIEW'; dateFrom: string; dateTo: string }
  | {
      kind: 'periodReapproval';
      mode: 'CAPTURE' | 'PAGE';
      batchId: string;
      after?: string;
      size?: 25 | 50 | 100;
      search?: string;
      filter?: 'ALL' | 'READY' | 'EXCLUDED' | 'APPROVED';
    }
  | {
      kind: 'periodReapproval';
      mode: 'EXECUTE' | 'RESUME';
      batchId: string;
      reviewedDigest: string;
      attested?: boolean;
    };
export type PeriodReapprovalMember = {
  id: string;
  display: { reference: string; type: string; person: string; date: string };
  inputs: Record<string, unknown>;
  unproven: boolean;
  outcome: 'READY' | 'EXCLUDED' | 'APPROVED';
  reason: string | null;
  result: { receiptId: string; revisionId: string; approvalHash: string } | null;
};
export type PeriodReapprovalPreview = {
  batchId: string;
  reviewedDigest: string;
  dateFrom: string;
  dateTo: string;
  started: boolean;
  readyCount: number;
  approvedCount: number;
  excludedCount: number;
  unprovenCount: number;
  members: PeriodReapprovalMember[];
  membershipComplete?: boolean;
  captured?: number;
  membershipTotal?: number;
  page?: {
    total: number;
    exact: true;
    size: 25 | 50 | 100;
    complete: boolean;
    next: string | null;
  };
};
export type PeriodReapprovalResult =
  | {
      success: true;
      periodReapproval: PeriodReapprovalPreview;
      backgroundCommand?: Omit<BackgroundCommand, 'leaseToken'>;
    }
  | { success: false; error: string; recovery?: 'START_NEW_REVIEW' };
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export function validPeriodReapprovalRequest(input: PeriodReapprovalRequest): boolean {
  if (input.mode === 'CAPTURE' || input.mode === 'PAGE')
    return (
      UUID.test(input.batchId) &&
      (input.after === undefined || input.after.length <= 512) &&
      (input.size === undefined || [25, 50, 100].includes(input.size))
    );
  if (input.mode === 'PREVIEW') {
    const ymd = /^\d{4}-\d{2}-\d{2}$/;
    return ymd.test(input.dateFrom) && ymd.test(input.dateTo) && input.dateFrom <= input.dateTo;
  }
  return (
    (input.mode === 'EXECUTE' || input.mode === 'RESUME') &&
    UUID.test(input.batchId) &&
    /^[0-9a-f]{64}$/.test(input.reviewedDigest) &&
    (input.mode !== 'EXECUTE' || input.attested === true)
  );
}
export function isPeriodReapprovalPreview(value: unknown): value is PeriodReapprovalPreview {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.batchId === 'string' &&
    UUID.test(v.batchId) &&
    typeof v.reviewedDigest === 'string' &&
    (/^[0-9a-f]{64}$/.test(v.reviewedDigest) ||
      (v.reviewedDigest === '' && v.membershipComplete === false)) &&
    (v.page === undefined || validMembershipPage(v)) &&
    typeof v.started === 'boolean' &&
    typeof v.dateFrom === 'string' &&
    typeof v.dateTo === 'string' &&
    ['readyCount', 'approvedCount', 'excludedCount', 'unprovenCount'].every(
      (k) => typeof v[k] === 'number' && Number.isSafeInteger(v[k]) && Number(v[k]) >= 0,
    ) &&
    Array.isArray(v.members) &&
    v.members.every(
      (m) =>
        m &&
        typeof m.id === 'string' &&
        ['READY', 'EXCLUDED', 'APPROVED'].includes(m.outcome) &&
        typeof m.unproven === 'boolean' &&
        m.display &&
        ['reference', 'type', 'person', 'date'].every((k) => typeof m.display[k] === 'string') &&
        m.inputs &&
        typeof m.inputs === 'object' &&
        !Array.isArray(m.inputs),
    )
  );
}
const REASONS: Record<string, string> = {
  IDENTITY_UNAVAILABLE:
    'Worker identity is unavailable. Ask an administrator to check the linked account.',
  SELF_APPROVAL: 'A different payroll officer must review your own timesheet.',
  NOT_UNBOUND_APPROVED: 'Only approved, unpaid timesheets outside a pay run can be re-approved.',
  PAYROLL_BOUND_OR_LOCKED: 'Payroll already holds, captured or settled this source.',
  GOVERNING_CORRECTION: 'A payroll correction needs ordinary review.',
  RECEIPT_UNAVAILABLE: 'Earlier approval evidence is unavailable.',
  EVIDENCE_INVALID: 'Earlier approval evidence could not be verified.',
  CAPTURED_VALUES_DIFFER: 'Values recorded in the earlier approval differ. Review individually.',
  FACT_REVIEW_REQUIRED: 'Pay facts need ordinary review.',
  APPROVAL_BREAK_UNRESOLVED: 'Break duration needs confirmation before fresh approval.',
  APPROVAL_NOT_WORKED: 'This shift was marked not worked. Review the source before approval.',
  APPROVAL_DAILY_VERIFICATION_REQUIRED:
    'Complete the assigned daily timesheet check before fresh approval.',
  APPROVAL_ASSIGNMENT_ACTOR_REQUIRED:
    'The named organisation administrator or payroll officer needs exactly one active linked staff record before fresh approval.',
  APPROVAL_ASSIGNED_TO_ANOTHER_USER:
    'The assigned payroll reviewer must approve this timesheet, or an administrator must resolve the assignment.',
  APPROVAL_AUTO_CLOSED: 'Confirm the missed clock-out before fresh approval.',
  APPROVAL_DURATION_INVALID: 'Correct the worked time before fresh approval.',
  APPROVAL_MULTIPLIER_INVALID: 'Recompute the pay multiplier before fresh approval.',
  APPROVAL_OVERLAP: 'Review the overlapping worked time before fresh approval.',
  APPROVAL_MANUAL_MAKER_REVIEW: 'Review who entered the manual allowance before fresh approval.',
  APPROVAL_MANUAL_ALLOWANCE_REVIEW:
    'Review the manual allowance evidence and eligibility before fresh approval.',
  APPROVAL_HANDOVER_CAP_REVIEW:
    'Review the handover allowance against the rolling limit before fresh approval.',
  APPROVAL_PH_REVIEW_REQUIRED:
    'Complete the public holiday worked-time check before fresh approval.',
  APPROVAL_DATA_INVALID: 'Correct invalid timesheet evidence before fresh approval.',
  SOURCE_UNAVAILABLE: 'The timesheet is unavailable.',
  REVIEW_CHANGED: 'The timesheet or related pay inputs changed after preview. Review individually.',
};
export function periodReapprovalReason(reason: string | null): string {
  return reason
    ? (REASONS[reason] ??
        (/^[A-Z][^\n]{10,180}\.$/.test(reason)
          ? reason
          : 'This timesheet needs individual review.'))
    : 'Ready for fresh approval';
}

function validMembershipPage(value: Record<string, unknown>): boolean {
  const p = value.page as Record<string, unknown> | null;
  return (
    !!p &&
    typeof p === 'object' &&
    p.exact === true &&
    [25, 50, 100].includes(Number(p.size)) &&
    Number.isSafeInteger(p.total) &&
    Number(p.total) >= 0 &&
    typeof p.complete === 'boolean' &&
    (p.complete ? p.next === null : typeof p.next === 'string') &&
    Array.isArray(value.members) &&
    value.members.length <= Number(p.size) &&
    typeof value.membershipComplete === 'boolean' &&
    Number.isSafeInteger(value.captured) &&
    Number.isSafeInteger(value.membershipTotal) &&
    Number(value.captured) <= Number(value.membershipTotal) &&
    (value.membershipComplete !== true || Number(value.captured) === Number(value.membershipTotal))
  );
}
